import * as React from 'react';
import Box from '@mui/material/Box';
import { columns, generateRows } from './partials';
import { Table, BillingAccountSelector } from '@organisms';
import {
  callGetBills,
  callUpdateBill,
  callDeleteBill,
  callGenerateBill,
  callGenerateBillArtifact,
  callSetBillApproval
} from '@api';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { TableContext, ApplicationContext } from '@particles';
import { Button, TextView } from '@atoms';
import { RouteComponentProps } from '@reach/router';
import {
  Grid,
  Popover,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateRangePicker, Range } from 'react-date-range';
import EventIcon from '@mui/icons-material/EventOutlined';
import { DateTime } from 'luxon';
import { materialTheme } from '@particles';
import { toast } from 'react-toastify';

// Client-side mirror of server's clampDayToMonthEnd / anchorOfMonth.
// Used to validate that the admin's picked endDate really is a valid
// cycle boundary for the selected account's billingDayOfMonth before we
// submit — same rule the server enforces in generateBillForDate.
const BILLING_ZONE = 'Europe/London';
const clampDayToMonthEnd = (year: number, month: number, day: number): number => {
  const daysInMonth =
    DateTime.fromObject({ year, month, day: 1 }, { zone: BILLING_ZONE }).daysInMonth ?? 28;
  return Math.min(Math.max(1, day), daysInMonth);
};
const anchorOfMonthAdmin = (billingDay: number, year: number, month: number): DateTime =>
  DateTime.fromObject(
    { year, month, day: clampDayToMonthEnd(year, month, billingDay) },
    { zone: BILLING_ZONE }
  ).startOf('day');
const isValidCycleEnd = (billingDay: number, endIso: DateTime): boolean => {
  const nextDay = endIso.plus({ days: 1 });
  const expected = anchorOfMonthAdmin(billingDay, nextDay.year, nextDay.month);
  return expected.toISODate() === nextDay.toISODate();
};

const BillsPage: React.FC<RouteComponentProps> = () => {
  const {
    state: {
      companyTransactionsLogsPagination: { page, perPage, orderBy }
    }
  } = useContext(TableContext);
  // Approver ID for the write. Slack-side approvals will come from the
  // future automation and pass their own userId; from the UI, we stamp
  // the currently signed-in SuperAdmin.
  const { state: appState } = useContext(ApplicationContext);
  const currentUserId: string | undefined = appState?.user?.id;

  const [start, setStart] = useState<Date>(DateTime.local().minus({ months: 3 }).toJSDate());
  const [end, setEnd] = useState<Date>(new Date());

  const [selectedAccount, setSelectedAccount] = useState<BillingAccount | null>(null);

  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editBill, setEditBill] = useState<Bill | null>(null);
  const [editStatus, setEditStatus] = useState<Bill['status']>('unpaid');
  const [editNotes, setEditNotes] = useState<string>('');
  const [editSaving, setEditSaving] = useState<boolean>(false);

  const [generatePrompt, setGeneratePrompt] = useState<{ bill: Bill; kind: 'invoice' | 'report' } | null>(null);
  const [generating, setGenerating] = useState<boolean>(false);

  // Manual bill-generation modal (backfill for a past cycle).
  const [genBillOpen, setGenBillOpen] = useState<boolean>(false);
  const [genBillAccount, setGenBillAccount] = useState<BillingAccount | null>(null);
  const [genBillEnd, setGenBillEnd] = useState<DateTime | null>(null);
  const [genBillSaving, setGenBillSaving] = useState<boolean>(false);

  // Delete-bill confirm dialog.
  const [deleteBillTarget, setDeleteBillTarget] = useState<Bill | null>(null);
  const [deletingBill, setDeletingBill] = useState<boolean>(false);

  // Manage-approvals dialog. Opens per-row from the Actions column and
  // handles both artifacts (invoice + report). `pendingDecline` tracks
  // which side (if any) is currently in decline-with-reason mode so the
  // reason input appears beneath the row being declined.
  const [approvalsBill, setApprovalsBill] = useState<Bill | null>(null);
  const [pendingDecline, setPendingDecline] = useState<'invoice' | 'report' | null>(null);
  const [declineReason, setDeclineReason] = useState<string>('');
  const [approvalSaving, setApprovalSaving] = useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const [range, setRange] = useState<Range[]>([
    {
      startDate: DateTime.local().minus({ months: 3 }).toJSDate(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);

  useEffect(() => {
    const date = new Date();
    const start = range[0]?.startDate ? range[0].startDate : date;
    const end = range[0]?.endDate ? range[0].endDate : date;

    const newEnd = new Date(end);
    newEnd.setHours(23);
    newEnd.setMinutes(59);
    newEnd.setSeconds(59);

    setStart(start);
    setEnd(end);
  }, [range]);

  const { startDate, endDate } = useMemo(() => ({
    startDate: DateTime.fromJSDate(start).toISO({ suppressMilliseconds: true, includeOffset: false }),
    endDate: DateTime.fromJSDate(end).toISO({ suppressMilliseconds: true, includeOffset: false })
  }), [start, end]);

  const transactionsQuery = useQuery({
    queryKey: [
      'bills',
      page,
      perPage,
      selectedAccount?.id ?? '',
      startDate,
      endDate,
      orderBy
    ],
    queryFn: () =>
      callGetBills({
        pageNumber: page,
        pageSize: perPage,
        companyName: selectedAccount?.companyName,
        startDate: startDate!,
        endDate: endDate!,
        sortBy: orderBy
      }),
    enabled: !!startDate && !!endDate,
    placeholderData: keepPreviousData
  });

  const billingAccounts = transactionsQuery.data?.code === 200 ? transactionsQuery.data.data : undefined;
  const error = transactionsQuery.data?.error;
  const loading = transactionsQuery.isFetching || csvLoading;

  const downloadCsv = async () => {
    setCsvLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_BASE;

      const queryString = `${page ? `pageNumber=${page}` : ``}
      ${perPage ? `&pageSize=${perPage}` : ``}
      ${startDate ? `&startDate=${startDate}` : ``}
      ${endDate ? `&endDate=${endDate}` : ``}
      ${orderBy ? `&sortBy=${orderBy}` : ``}
      ${selectedAccount?.companyName ? `&companyName=${encodeURIComponent(selectedAccount.companyName)}` : ``}`;

      const urlApi = `${apiBase}/bills?${queryString}&downloadCsv=true`;

      const zone: string[] = DateTime.now().toFormat('ZZ').split(':');
      const hour = Number(zone[0]);
      const mint = Number(zone[1]);
      const totalMint: number = hour * 60 + mint;

      const fetchOptions: RequestInit = {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
          timezone: `${totalMint}`
        },
        method: 'GET'
      };

      const response: Response = await fetch(urlApi, fetchOptions);

      if (response.status == 200) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'MonthlyLogs.csv');
        document.body.appendChild(link);
        link.click();
      } else {
        toast('Something went wrong');
      }
    } finally {
      setCsvLoading(false);
    }
  };

  const handleEditBill = (token: Bill) => {
    setEditBill(token);
    setEditStatus(token.status ?? 'unpaid');
    setEditNotes(token.notes ?? '');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    if (editSaving) return;
    setEditDialogOpen(false);
    setEditBill(null);
    setEditNotes('');
  };

  const handleSaveEditBill = async () => {
    if (!editBill) return;
    setEditSaving(true);
    try {
      const res = await callUpdateBill({
        id: editBill.id,
        status: editStatus,
        notes: editNotes.trim() === '' ? null : editNotes
      });
      if (res.code === 200 || res.code === 201) {
        toast.success('Bill updated');
        setEditDialogOpen(false);
        setEditBill(null);
        setEditNotes('');
        transactionsQuery.refetch();
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        toast.error(res.error?.message ?? 'Failed to update bill');
      }
    } catch (e) {
      toast.error('Failed to update bill');
    } finally {
      setEditSaving(false);
    }
  };

  /**
   * Icon click handler for invoice / report. If the artifact already
   * exists we open it directly; if not, prompt the operator to
   * generate it on demand. Regeneration is always available via the
   * dialog if they want to overwrite an existing one.
   */
  const handleOpenArtifact = (bill: Bill, kind: 'invoice' | 'report') => {
    const fileName = kind === 'invoice' ? bill.invoiceUrl : bill.reportUrl;
    if (fileName) {
      window.open(`${process.env.REACT_APP_API_BASE}/a/${fileName}`, '_blank');
      return;
    }
    setGeneratePrompt({ bill, kind });
  };

  const closeGeneratePrompt = () => {
    if (generating) return;
    setGeneratePrompt(null);
  };

  const confirmGenerateArtifact = async () => {
    if (!generatePrompt) return;
    const { bill, kind } = generatePrompt;
    setGenerating(true);
    try {
      const res = await callGenerateBillArtifact({ id: bill.id, kind });
      if ((res.code === 200 || res.code === 201) && res.data?.shareUrl) {
        toast.success(`${kind === 'invoice' ? 'Invoice' : 'Report'} generated`);
        // shareUrl is returned as a host-agnostic path (/a/{fileName}) —
        // prepend the API base so the new tab opens the actual artifact.
        window.open(`${process.env.REACT_APP_API_BASE}${res.data.shareUrl}`, '_blank');
        setGeneratePrompt(null);
        transactionsQuery.refetch();
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        toast.error(res.error?.message ?? `Failed to generate ${kind}`);
      }
    } catch (e) {
      toast.error(`Failed to generate ${kind}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteBill = (bill: Bill) => setDeleteBillTarget(bill);

  const confirmDeleteBill = async () => {
    if (!deleteBillTarget) return;
    setDeletingBill(true);
    try {
      const res = await callDeleteBill(deleteBillTarget.id);
      if (res.code === 200 || res.code === 201) {
        toast.success('Bill deleted');
        setDeleteBillTarget(null);
        transactionsQuery.refetch();
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        toast.error(res.error?.message ?? 'Failed to delete bill');
      }
    } catch (e) {
      toast.error('Failed to delete bill');
    } finally {
      setDeletingBill(false);
    }
  };

  const handleManageApprovals = (bill: Bill) => {
    setApprovalsBill(bill);
    setPendingDecline(null);
    setDeclineReason('');
  };

  const closeApprovalsDialog = () => {
    if (approvalSaving) return;
    setApprovalsBill(null);
    setPendingDecline(null);
    setDeclineReason('');
  };

  const submitApproval = async (
    kind: 'invoice' | 'report',
    action: 'approve' | 'decline',
    reason?: string
  ) => {
    if (!approvalsBill) return;
    if (!currentUserId) {
      toast.error('Cannot record approval — no signed-in user id');
      return;
    }
    setApprovalSaving(true);
    try {
      const res = await callSetBillApproval({
        id: approvalsBill.id,
        kind,
        action,
        approvedByUserId: currentUserId,
        via: 'admin',
        reason: action === 'decline' ? reason ?? '' : null
      });
      if (res.code === 200 || res.code === 201) {
        toast.success(
          `${kind === 'invoice' ? 'Invoice' : 'Report'} ${action === 'approve' ? 'approved' : 'declined'}`
        );
        setApprovalsBill(null);
        setPendingDecline(null);
        setDeclineReason('');
        transactionsQuery.refetch();
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        toast.error(res.error?.message ?? `Failed to ${action} ${kind}`);
      }
    } catch (e) {
      toast.error(`Failed to ${action} ${kind}`);
    } finally {
      setApprovalSaving(false);
    }
  };

  const rowData: TableRow[] = billingAccounts
    ? generateRows(
        billingAccounts.data,
        handleEditBill,
        handleOpenArtifact,
        handleDeleteBill,
        handleManageApprovals
      )
    : [];

  return (
    <Box m={4}>
      <Stack>
        <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', mb: 2 }}>
          <BillingAccountSelector
            title="Filter by Billing Account"
            status="both"
            value={selectedAccount}
            onSelected={setSelectedAccount}
            caller="bills"
          />
          <Box sx={{ flex: 1 }} />
          <Grid item xl={10} sm={12} sx={{ justifyContent: 'flex-end', alignItems: 'end' }}>
            <Grid mb={2} spacing={2} container>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}>
                <DateRangePicker
                  onChange={(item) => setRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  months={2}
                  ranges={range}
                  direction="horizontal"
                />
              </Popover>
              <Grid item>
                <Button
                  aria-describedby={id}
                  sx={{ paddingLeft: 5, color: materialTheme.palette.text.primary }}
                  startIcon={<EventIcon />}
                  variant={'outlined'}
                  onClick={handleClick}>
                  <TextView $fontWeight={400}>
                    {DateTime.fromJSDate(
                      range[0].startDate ? range[0]?.startDate : new Date()
                    ).toFormat('LLLL, dd yyyy')}
                    {` - `}
                    {DateTime.fromJSDate(
                      range[0].endDate ? range[0]?.endDate : new Date()
                    ).toFormat('LLLL, dd yyyy')}
                  </TextView>
                </Button>
              </Grid>
              <Grid item>
                <Button
                  label="Generate bill"
                  onClick={() => {
                    setGenBillAccount(selectedAccount);
                    setGenBillEnd(null);
                    setGenBillOpen(true);
                  }}
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <Button label="Export" onClick={downloadCsv} size="small" />
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Stack>

      <Table
        name="company-transactions-logs"
        columns={columns}
        data={rowData}
        error={error}
        isLoading={loading}
        pagination={billingAccounts}
        persistColumnPrefs
      />

      {/* Confirm generate invoice/report artifact for an existing bill */}
      <Dialog open={!!generatePrompt} onClose={closeGeneratePrompt} maxWidth="xs" fullWidth>
        <DialogTitle>Generate {generatePrompt?.kind === 'invoice' ? 'Invoice' : 'Report'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            <TextView>
              {generatePrompt
                ? `Do you want to create the ${
                    generatePrompt.kind === 'invoice' ? 'invoice' : 'report'
                  } for ${generatePrompt.bill.companyName ?? 'this bill'}?`
                : ''}
            </TextView>
            <TextView $fontSize={12} $fontColor="rgba(0,0,0,0.6)">
              We&apos;ll build it from this bill&apos;s data, save it against the row, and open it in a new tab.
            </TextView>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={closeGeneratePrompt} variant="outlined" />
          <Button
            label={generating ? 'Generating…' : 'Generate'}
            onClick={confirmGenerateArtifact}
          />
        </DialogActions>
      </Dialog>

      {/* Generate a bill for a specific past cycle (backfill). */}
      <Dialog
        open={genBillOpen}
        onClose={() => (genBillSaving ? undefined : setGenBillOpen(false))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate bill</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextView $fontSize={13} $fontColor="rgba(0,0,0,0.7)">
              Backfill a bill for a past cycle. This never changes the account&apos;s live billing
              schedule — the daily cron continues on its own.
            </TextView>
            <BillingAccountSelector
              title="Billing account"
              status="both"
              value={genBillAccount}
              onSelected={setGenBillAccount}
              caller="bills-generate"
            />
            <DatePicker
              label="Cycle end date"
              value={genBillEnd}
              onChange={setGenBillEnd}
              maxDate={DateTime.now().minus({ days: 1 })}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            {genBillAccount &&
              typeof genBillAccount.billingDayOfMonth === 'number' &&
              genBillEnd &&
              !isValidCycleEnd(genBillAccount.billingDayOfMonth, genBillEnd) && (
                <TextView $fontSize={12} $fontColor="#B91C1C">
                  Not a valid cycle end for billing day {genBillAccount.billingDayOfMonth}. Pick the
                  day before the next anchor (e.g. billing day 28 → cycle ends on the 27th).
                </TextView>
              )}
            {genBillAccount && typeof genBillAccount.billingDayOfMonth !== 'number' && (
              <TextView $fontSize={12} $fontColor="#B91C1C">
                This account has no billingDayOfMonth set — set one on the account first.
              </TextView>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            variant="outlined"
            onClick={() => setGenBillOpen(false)}
          />
          <Button
            label={genBillSaving ? 'Generating…' : 'Generate'}
            disabled={
              !genBillAccount ||
              !genBillEnd ||
              typeof genBillAccount.billingDayOfMonth !== 'number' ||
              !isValidCycleEnd(genBillAccount.billingDayOfMonth, genBillEnd) ||
              genBillSaving
            }
            onClick={async () => {
              if (!genBillAccount || !genBillEnd) return;
              setGenBillSaving(true);
              try {
                const res = await callGenerateBill({
                  billingAccountId: genBillAccount.id,
                  endDate: genBillEnd.toFormat('yyyy-LL-dd')
                });
                if (res.code === 200 || res.code === 201) {
                  toast.success('Bill generated');
                  setGenBillOpen(false);
                  transactionsQuery.refetch();
                } else {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  toast.error(res.error?.message ?? 'Failed to generate bill');
                }
              } catch (e) {
                toast.error('Failed to generate bill');
              } finally {
                setGenBillSaving(false);
              }
            }}
          />
        </DialogActions>
      </Dialog>

      {/* Edit Bill (status + notes) Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Bill</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="edit-bill-status-label">Status</InputLabel>
              <Select
                labelId="edit-bill-status-label"
                label="Status"
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value as Bill['status'])}>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="in_progress">In progress</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="void">Void</MenuItem>
              </Select>
            </FormControl>
            <MuiTextField
              label="Notes"
              name="notes"
              fullWidth
              multiline
              minRows={3}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Optional remarks"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={handleCloseEditDialog} variant="outlined" />
          <Button label={editSaving ? 'Saving…' : 'Save'} onClick={handleSaveEditBill} />
        </DialogActions>
      </Dialog>

      {/* Manage approvals (invoice + report). Displays current state
          and lets the operator approve or decline either artifact. A
          decline requires a non-empty reason (enforced by the server;
          the input appears inline when a decline is pending). */}
      <Dialog
        open={Boolean(approvalsBill)}
        onClose={closeApprovalsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage approvals</DialogTitle>
        <DialogContent>
          {approvalsBill && (
            <Stack spacing={3} sx={{ pt: 1 }}>
              {(['invoice', 'report'] as const).map((kind) => {
                const status = approvalsBill[
                  kind === 'invoice' ? 'invoiceApprovalStatus' : 'reportApprovalStatus'
                ];
                const approvedByName = approvalsBill[
                  kind === 'invoice' ? 'invoiceApprovedByName' : 'reportApprovedByName'
                ];
                const approvedAt = approvalsBill[
                  kind === 'invoice' ? 'invoiceApprovedAt' : 'reportApprovedAt'
                ];
                const via = approvalsBill[
                  kind === 'invoice' ? 'invoiceApprovedVia' : 'reportApprovedVia'
                ];
                const reason = approvalsBill[
                  kind === 'invoice' ? 'invoiceDeclineReason' : 'reportDeclineReason'
                ];
                const hasArtifact = Boolean(
                  approvalsBill[kind === 'invoice' ? 'invoiceUrl' : 'reportUrl']
                );
                const inDeclineMode = pendingDecline === kind;

                return (
                  <Box key={kind}>
                    <TextView $fontWeight={600}>
                      {kind === 'invoice' ? 'Invoice' : 'Performance report'}
                    </TextView>
                    <TextView $fontSize={13} $fontColor="rgba(0,0,0,0.7)">
                      Status: {status}
                      {approvedByName ? ` · by ${approvedByName}` : ''}
                      {approvedAt
                        ? ` · ${DateTime.fromISO(approvedAt).toFormat('dd LLL yyyy, HH:mm')}`
                        : ''}
                      {via ? ` · via ${via}` : ''}
                    </TextView>
                    {status === 'declined' && reason ? (
                      <TextView $fontSize={12} $fontColor="#B91C1C">
                        Reason: {reason}
                      </TextView>
                    ) : null}
                    {!hasArtifact ? (
                      <TextView $fontSize={12} $fontColor="rgba(0,0,0,0.5)">
                        Artifact not generated yet — approval unavailable.
                      </TextView>
                    ) : null}
                    {inDeclineMode ? (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        <MuiTextField
                          label="Decline reason"
                          fullWidth
                          multiline
                          minRows={2}
                          value={declineReason}
                          onChange={(e) => setDeclineReason(e.target.value)}
                          placeholder="Required — why is this being declined?"
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            label={approvalSaving ? 'Saving…' : 'Confirm decline'}
                            onClick={() => submitApproval(kind, 'decline', declineReason)}
                          />
                          <Button
                            label="Cancel"
                            variant="outlined"
                            onClick={() => {
                              setPendingDecline(null);
                              setDeclineReason('');
                            }}
                          />
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          label="Approve"
                          size="small"
                          onClick={() => submitApproval(kind, 'approve')}
                          disabled={!hasArtifact || approvalSaving}
                        />
                        <Button
                          label="Decline"
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setPendingDecline(kind);
                            setDeclineReason('');
                          }}
                          disabled={!hasArtifact || approvalSaving}
                        />
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button label="Close" variant="outlined" onClick={closeApprovalsDialog} />
        </DialogActions>
      </Dialog>

      {/* Confirm bill delete (cascades to invoice + report artifacts). */}
      <Dialog
        open={Boolean(deleteBillTarget)}
        onClose={() => (deletingBill ? undefined : setDeleteBillTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete bill?</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            <TextView>
              {deleteBillTarget
                ? `Permanently delete the bill for ${deleteBillTarget.companyName ?? 'this account'} covering ${
                    deleteBillTarget.startDate ?? ''
                  } → ${deleteBillTarget.endDate ?? ''}?`
                : ''}
            </TextView>
            <TextView $fontSize={12} $fontColor="rgba(0,0,0,0.6)">
              This also deletes the associated invoice and performance-report artifacts. This
              action cannot be undone.
            </TextView>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            variant="outlined"
            onClick={() => setDeleteBillTarget(null)}
          />
          <Button
            label={deletingBill ? 'Deleting…' : 'Delete'}
            onClick={confirmDeleteBill}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillsPage;
